import {createSlice} from '@reduxjs/toolkit'
import text from './text.json'
import AuthService from '../../api/auth'
import CustomerService from '../../api/customer'
import AppsumoService from '../../api/appsumo'
import tokens from '../../utils/tokens'
import localStorageManager from '../../utils/localStorageManager'
import Navigation from '../../utils/navigation'
import {limits} from '../../utils/limits'
import localStorageKeys from '../../utils/localStorageKeys'
import {getServices} from '../resources'
import {setGlobalInfo} from '../sidebar'
import {toast} from 'react-toastify'
import {routes} from '../../routes'
import {setSelectedTeamId} from '../teams'
import {doNothing} from '../../utils/placeholder'
import plans from '../../data/plans.json'

export const authSlice = createSlice({
    name: 'auth',
    initialState: {
        isAuthenticated: false,
        logoutIsProcessing: false,
        loaderText: text.page_is_loading,
        gtmPurchaseInfo: null,
        organisationLimitUsages: [],
        isUsageLoading: false
    },
    reducers: {
        setIsAuthenticated: (state) => {
            state.isAuthenticated = true
        },
        setLogOutIsProcessing: (state) => {
            state.logoutIsProcessing = true
        },
        setLoaderText: state => {
            state.loaderText = text.authorization_failed
        },
        setGtmPurchaseInfo: (state, {payload}) => {
            state.gtmPurchaseInfo = payload
        },
        setOrganisationLimitUsages: (state, {payload}) => {
            state.organisationLimitUsages = payload
        },
        setIsUsageLoading: (state, {payload}) => {
            state.isUsageLoading = payload
        }
    }
})

export const {
    setIsAuthenticated,
    setLoaderText,
    setLogOutIsProcessing,
    setGtmPurchaseInfo,
    setAppsumoActivationStatus,
    setOrganisationLimitUsages,
    setIsUsageLoading
} = authSlice.actions

export default authSlice.reducer

export const getCustomer = () => {
    return async dispatch => {
        try {
            let customerData = await CustomerService.getCustomerData()
            customerData = customerData.data.data
            localStorageManager.set('customer', customerData?.customer)
            localStorageManager.set(localStorageKeys.TEAM, customerData?.team)

            if (!localStorage.getItem(localStorageKeys.TEAM_ID)) {
                localStorage.setItem(localStorageKeys.TEAM_ID, customerData?.team?.team_id)
                dispatch(setSelectedTeamId(customerData?.team?.team_id))
            }
            if (!localStorageManager.get(localStorageKeys.IS_DARK_MODE)) {
                localStorageManager.set(localStorageKeys.IS_DARK_MODE, false)
            }

            localStorageManager.set(limits.limits, customerData?.limits)
            localStorage.setItem(localStorageKeys.PLAN, customerData?.organisation?.plan)
            localStorage.setItem('upgradedPlan', customerData?.organisation?.plan)
            localStorageManager.set(localStorageKeys.TRIAL, customerData?.organisation?.trial)
            const overriddenLimits = customerData?.organisation?.overridden_limits
            if (Array.isArray(overriddenLimits)) {
                localStorageManager.set(limits.overriddenLimits, overriddenLimits)
            }
            dispatch(setGtmPurchaseInfo(customerData?.organisation?.gtm_purchase_info_sent))
        } catch (e) {
            doNothing()
        }
    }
}

export const upgradeOrganisationPlan = navigate => {
    return async dispatch => {
        dispatch(setGlobalInfo(text.plan_upgrading))
        try {
            const {data} = await CustomerService.upgradeOrganisationPlan()
            setTimeout(async() => {
                await dispatch(getCustomer())
                dispatch(setGlobalInfo(''))
                navigate(Navigation.getLocalizedPath(routes.plans.paths))
                toast.success(data.message)
            }, 2000)
        } catch (e) {
            dispatch(setGlobalInfo(''))
            toast.error(e?.response?.data?.message)
        }
    }
}

export const getAllLimitUsages = () => {
    return async dispatch => {
        dispatch(setIsUsageLoading(true))
        try {
            const {data} = await CustomerService.getOrganisationLimitUsages()
            dispatch(setOrganisationLimitUsages(data?.data?.limits))
        } catch {
            doNothing()
        } finally {
            dispatch(setIsUsageLoading(false))
        }
    }
}

export const checkAppsumoActivation = (navigate, token) => {
    return async dispatch => {
        try {
            const {data} = await AppsumoService.checkActivation(token)
            if (data?.data?.activation?.status === 'created') {
                dispatch(upgradeOrganisationPlan(navigate))
            }
            localStorage.setItem(localStorageKeys.APPSUMO_INVOICE_UUID, data?.data?.activation?.invoice_item_uuid)
        } catch (e) {
            localStorage.removeItem(localStorageKeys.APPSUMO_INVOICE_UUID)
            return false
        }
    }
}

export const userAuthorization = (navigate, formData) => {
    return async dispatch => {
        try {
            const {data} = await AuthService.auth(formData)
            const userData = data?.data
            localStorage.setItem(tokens.token.key, userData?.token)
            localStorage.setItem(tokens.accessToken.key, userData?.access_token)
            await dispatch(getCustomer())
            await dispatch(getServices())
            await dispatch(setIsAuthenticated())
            const plan = localStorage.getItem(localStorageKeys.PLAN)
            if (plan === plans.free_v2.key) {
                dispatch(checkAppsumoActivation(navigate, userData?.token))
            }
            return userData?.access_token
        } catch (e) {
            dispatch(setLoaderText())
            Navigation.navigateToSignIn()
        }
    }
}

export const userLogOut = () => {
    return async dispatch => {
        dispatch(setLogOutIsProcessing())
        try {
            await AuthService.logOut()
            localStorage.clear()
            Navigation.navigateToSignIn()
        } catch (e) {
            doNothing()
        }
    }
}

export const getTokens = () => {
    return async dispatch => {
        try {
            const data = localStorageManager.get('customer')
            const customerId = data?.customer_id
            await dispatch(userAuthorization(null, {customer_id: customerId}))
        } catch (e) {
            Navigation.navigateToSignIn()
        }
    }
}
